import { useEffect, useState, useCallback, useMemo } from "react"
import { fetchQuizSessions } from "../services/quizSessionService"

export function useQuizResponses(quizId, options = {}) {
  const { enabled = true } = options
  const [sessions, setSessions] = useState([])
  const [quizData, setQuizData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [nextUrl, setNextUrl] = useState(null)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [responseCount, setResponseCount] = useState(0)

  const fetchResponses = useCallback(
    async (url) => {
      if (!quizId && !url) return

      try {
        if (!url) setLoading(true)
        else setIsFetchingMore(true)

        // expected { results, quiz, next, previous } from backend
        const { results, quiz, next, count } = await fetchQuizSessions({ quizId, quizUrl: url })
        
        setResponseCount(count)
        setSessions((prev) => (url ? [...prev, ...results] : results))
        if (quiz) setQuizData(quiz)
        setNextUrl(next)
      } catch (err) {
        console.error(err)
        setError("Failed to load quiz responses")
      } finally {
        setLoading(false)
        setIsFetchingMore(false)
      }
    },
    [quizId]
  )

  useEffect(() => {
    if (!enabled) return
    fetchResponses()
  }, [quizId, enabled, fetchResponses])

  const loadMore = () => {
    if (nextUrl) fetchResponses(nextUrl)
  }

  const averageScore = useMemo(() => {
    if (sessions.length === 0) return 0
    const total = sessions.reduce((sum, session) => {
      const correct = Number(session.correct_answer_count) || 0
      const count = Number(session.total_question_count) || 0
      const score = count > 0 ? (correct / count) * 100 : 0
      return sum + score
    }, 0)
    return Math.round(total / sessions.length)
  }, [sessions])

  return {
    sessions,
    quizData,
    loading,
    error,
    responseCount,
    averageScore,
    loadMore,
    hasMore: !!nextUrl,
    isFetchingMore,
  }
}
